import React, { useRef, useEffect } from "react"
import { Props as focusTrapProps } from "focus-trap-react"

import { Modal } from "@tenforce/toolbox-modal-react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { ColorName } from "@tenforce/toolbox-default-style"

import domCleanupManager from "../../../util/customview/DomCleanUpManager"
import { GlobalState } from "../../../store"
import { Labels } from "../../../util/Translations"
import { useAppDispatch, useAppSelector } from "../../../hooks"

import { setIsVisible } from "../slice"
import { selectAIModalById } from "../selectors"
import { IId } from ".."

import AIModalContent from "./AIModalContent"

export interface Props extends IId {
    buttonActionType?: ActionType
    buttonLabel?: string
    buttonTextColor?: ColorName
    isButtonLabelShown?: boolean
    isButtonShown?: boolean
    isButtonTransparent?: boolean
}

export default function AIModal({
    id,
    buttonActionType = ActionType.button,
    buttonLabel = Labels.AskAI,
    buttonTextColor = ColorName.Goldenrod,
    isButtonLabelShown = true,
    isButtonShown = true,
    isButtonTransparent = true,
}: Props): JSX.Element {
    const AIModalData = useAppSelector((state) => selectAIModalById(state, id))

    if (!AIModalData) {
        return <></>
    }

    // ensure the RTF css bleeding is handled
    const componentContainerRef = useRef<HTMLDivElement | null>(null)
    const cleanupHandleRef = useRef<DomCleanupHandle | null>(null)
    useEffect(() => {
        if (!componentContainerRef.current) {
            return
        }

        const handle = domCleanupManager.register({
            element: componentContainerRef.current,
        })

        cleanupHandleRef.current = handle

        return (): void => {
            handle.unregister()
            cleanupHandleRef.current = null
        }
    }, [])

    const fieldsData = useAppSelector((state: GlobalState) => state.fields)

    useEffect(() => {
        if (fieldsData.isInitialized) {
            cleanupHandleRef.current?.cleanup()
        }
    }, [
        fieldsData.isInitialized,
        fieldsData.isLoading,
        fieldsData.currentFieldInEditMode,
    ])

    const { isVisible } = AIModalData

    const dispatch = useAppDispatch()
    const containerRef = useRef<HTMLDivElement>(null)

    const modalId = "AIModal"
    // we want focus on the modal, but not any of the buttons in it
    // this is how we also avoid in the Lightbox focusing on the delete button
    // without the fallbackFocus the component would throw an error
    const focusTrap: Partial<focusTrapProps> = {
        focusTrapOptions: {
            initialFocus: containerRef.current ?? `#${modalId}`,
            fallbackFocus: `#${modalId}`,
            // these are here to handle the bug with not being able to click
            // on notifications rendered above the modal
            allowOutsideClick: true,
            clickOutsideDeactivates: false,
        },
    }

    const closeModal = (): void => {
        dispatch(setIsVisible({ id, value: false }))
    }

    return (
        <div data-ft-component ref={componentContainerRef}>
            {isButtonShown && (
                <Action
                    actionType={buttonActionType}
                    data-ft-id="aiTriggerButton"
                    data-ft-aiid={id}
                    isTransparent={isButtonTransparent}
                    textColor={buttonTextColor}
                    onClick={(): void => {
                        // some actions close the modal
                        // so we cannot have it within the internal state
                        dispatch(setIsVisible({ id, value: true }))
                    }}
                    title={buttonLabel}
                    extraClasses="animate-ai-border"
                >
                    {Symbols.Shortcut}
                    {!!isButtonLabelShown && <>&nbsp;{buttonLabel}</>}
                </Action>
            )}
            <Modal
                isShown={isVisible}
                containerProps={{
                    id: modalId,
                    isAbsolutePositioned: true,
                    onClose: closeModal,
                }}
                innerContentProps={{
                    extraClasses: "tw-w-1/4 tw-h-screen tw-fixed tw-right-0",
                }}
                focusTrapProps={focusTrap}
                overlayProps={{
                    onClick: closeModal,
                }}
            >
                <div
                    className="bg-white theme-bubble tw-m-2 tw-h-full tw-flex"
                    ref={containerRef}
                    tabIndex={-1}
                >
                    <AIModalContent id={id} />
                </div>
            </Modal>
        </div>
    )
}
